import {FC, RefObject} from 'react';
import {Box, Button, Flex, Stack} from '@chakra-ui/react';
import {api, useT} from '@kcast-fe/core';
import {Controller, useForm} from 'react-hook-form';
import {Input, Select, Textarea, useScreenSize} from 'ui-kit';

interface IProps {
  ref: RefObject<HTMLDivElement | null>;
  onClose: () => void;
  onCreated?: (id: string) => void;
}

interface ISummaryCreateForm {
  title: string;
  prompt: string;
  model_name: string;
  wiring_prefix: string;
  wiring_suffix: string;
  update_every_n_turns: number;
  char_limit: number;
  max_attempts: number;
}

const DEFAULT_VALUES: ISummaryCreateForm = {
  title: '',
  prompt: '',
  model_name: 'glm-5.1-chat',
  wiring_prefix: '[USER MEMORY (compact summary)]',
  wiring_suffix: '[END USER MEMORY]',
  update_every_n_turns: 20,
  char_limit: 4000,
  max_attempts: 2
};

const SummaryCreateModalFC: FC<IProps> = ({ref, onClose, onCreated}) => {
  const createMutation = api.summary.useCreateSummaryMutation();
  const {data: models = []} = api.catalogs.useModelsQuery();

  const {isLessThan768} = useScreenSize();
  const {t} = useT();

  const {
    register,
    control,
    handleSubmit,
    formState: {isValid}
  } = useForm<ISummaryCreateForm>({defaultValues: DEFAULT_VALUES});

  const onSubmit = (data: ISummaryCreateForm) => {
    const body: Parameters<typeof createMutation.mutate>[0] = {
      title: data.title.trim(),
      prompt: data.prompt,
      model_name: data.model_name,
      wiring_prefix: data.wiring_prefix,
      wiring_suffix: data.wiring_suffix,
      update_every_n_turns: data.update_every_n_turns,
      char_limit: data.char_limit,
      max_attempts: data.max_attempts
    };
    createMutation.mutate(body, {
      onSuccess: (result) => {
        onCreated?.(result.id);
      }
    });
  };

  return (
    <Stack h={'100%'} gap={'0'}>
      <Box flex={'1 1 auto'} overflowY={'auto'} p={'16px 24px'}>
        <Stack gap={'16px'}>
          <Box>
            <Box mb={'6px'}>{t('summary.fields.title')}</Box>
            <Input
              size={'lg'}
              placeholder={t('summary.placeholders.title')}
              {...register('title', {
                required: true,
                maxLength: {value: 200, message: t('summary.validation.titleMaxLength')},
                validate: (v) => !!v.trim() || t('summary.validation.titleRequired')
              })}
            />
          </Box>

          <Box>
            <Box mb={'6px'}>{t('summary.fields.updatePrompt')}</Box>
            <Controller
              name={'prompt'}
              control={control}
              render={({field}) => (
                <Textarea
                  rows={6}
                  size={'lg'}
                  autoresize={false}
                  value={field.value}
                  placeholder={t('summary.placeholders.updatePrompt')}
                  onChange={field.onChange}
                />
              )}
            />
          </Box>

          <Controller
            control={control}
            name={`model_name`}
            render={({field: {onChange, value}}) => (
              <Box>
                <Box mb={'6px'}>{t('fields.model')}</Box>
                <Select
                  value={value}
                  onChange={onChange}
                  container={ref}
                  options={models.map((m) => ({value: m, label: m}))}
                />
              </Box>
            )}
          />

          <Box>
            <Box mb={'6px'}>{t('summary.fields.wiringPrefix')}</Box>
            <Input size={'lg'} {...register('wiring_prefix')} />
          </Box>

          <Box>
            <Box mb={'6px'}>{t('summary.fields.wiringSuffix')}</Box>
            <Input size={'lg'} {...register('wiring_suffix')} />
          </Box>

          <Flex gap={'12px'} flexDirection={isLessThan768 ? 'column' : 'row'}>
            <Box flex={1}>
              <Box mb={'6px'}>{t('summary.fields.updateEveryTurns')}</Box>
              <Input
                min={1}
                size={'lg'}
                type={'number'}
                {...register('update_every_n_turns', {
                  valueAsNumber: true,
                  min: {value: 1, message: t('summary.validation.minValue')}
                })}
              />
            </Box>
            <Box flex={1}>
              <Box mb={'6px'}>{t('summary.fields.charLimit')}</Box>
              <Input
                min={1}
                size={'lg'}
                type={'number'}
                {...register('char_limit', {
                  valueAsNumber: true,
                  min: {value: 1, message: t('summary.validation.minValue')}
                })}
              />
            </Box>
            <Box flex={1}>
              <Box mb={'6px'}>{t('summary.fields.maxAttempts')}</Box>
              <Input
                size={'lg'}
                type={'number'}
                min={0}
                {...register('max_attempts', {valueAsNumber: true})}
              />
            </Box>
          </Flex>
        </Stack>
      </Box>

      <Flex
        gap={'12px'}
        justifyContent={'flex-end'}
        p={'24px 24px 12px 24px'}
        borderTop={'thin solid'}
        borderColor={'{colors.bgMain}'}
      >
        <Button
          size={'lg'}
          variant={'subtle'}
          disabled={createMutation.isPending}
          onClick={onClose}
        >
          {t('actions.cancel')}
        </Button>
        <Button
          size={'lg'}
          variant={'solid'}
          disabled={createMutation.isPending || !isValid}
          onClick={handleSubmit(onSubmit)}
        >
          {t('summary.actions.create')}
        </Button>
      </Flex>
    </Stack>
  );
};

export const SummaryCreateModal = SummaryCreateModalFC;
